import { setUser } from 'app/app/bugsnag/bugsnag.client'
import { ApiMasterData, UserSettings } from 'app/types/backend-api'
import {
  AppConfig,
  ClientConfigData,
  ClientFrontlineConfig,
  ClientProfile,
  FrontlineConfigJson,
  OfferingQuestions
} from 'app/types/types'
import { t } from 'i18next'
import moment from 'moment'
import i18nInit from 'app/app/(client)/i18n/i18n'
import { getConfigData } from 'app/services/client/client-api'
import { User } from 'app/app/bugsnag/bugsnag.client'
import { Args } from 'app/types/redux-types'
import { ThunkAPI } from '../../../ReduxSlice'
import { fetchConfigWithPromise, updateConfigurationsPromise } from 'app/app/(client)/db/db'
import { Constants } from 'app/app/(client)/utils/client-constants'
import { setUserData } from 'app/app/(client)/utils/analytics-tracker'
import { withThunkHandler } from 'app/app/(client)/utils/async-handler'

const fetchMasterDataHandler = async (_thunkAPI?: ThunkAPI, _args?: Args): Promise<AppConfig> => {
  const serverData = await getConfigData()
  const data = await updateMasterData(serverData)

  i18nInit(data.frontlineConfig.i18n, data.settings.language, data.environment)

  await initializeStores(data)

  const updatedFrontlineConfig: ClientFrontlineConfig = {
    ...data.frontlineConfig,
    offeringQuestions: {} as OfferingQuestions,
    pricingConfig: {}
  }
  const updatedData: ClientConfigData = { ...data, frontlineConfig: updatedFrontlineConfig }
  return {
    frontLineConfig: updatedFrontlineConfig,
    profile: updatedData.profile,
    settings: updatedData.settings
  } as AppConfig
}

export const fetchMasterDataAsync = withThunkHandler(fetchMasterDataHandler)

const updateMasterData = async (masterData: ApiMasterData) => {
  const { FRONT_LINE_CONFIG, PROFILE, SYNC_TIME_STAMP, SETTINGS } = Constants
  if (masterData) {
    return masterData
  } else {
    const [frontlineConfig, profile, timestamp, settings] = await Promise.all([
      fetchConfigWithPromise<FrontlineConfigJson>(FRONT_LINE_CONFIG),
      fetchConfigWithPromise<ClientProfile>(PROFILE),
      fetchConfigWithPromise(SYNC_TIME_STAMP),
      fetchConfigWithPromise<UserSettings>(SETTINGS)
    ])
    const { syncTimestamp } = timestamp as { syncTimestamp: string }
    if (!syncTimestamp) {
      const error = new Error(`Cannot fetch data from server and no locally cached data available.
                  Make sure you are connected to the internet.`)
      error.name = 'Initial data error'
      throw error
    } else if (moment(syncTimestamp).isBefore(moment().subtract(Constants.OFFLINE_LIMIT, 'hours'))) {
      const error = new Error(t('offline.dataTooOld'))
      error.name = 'Expired data error'
      throw error
    }
    return {
      environment: '',
      frontlineConfig: frontlineConfig as FrontlineConfigJson,
      profile,
      settings
    } as ApiMasterData
  }
}

const initializeStores = async (data: ApiMasterData) => {
  const { frontlineConfig, profile, settings } = data

  setUserData(profile)
  const { userId, displayName, salesOrganization } = profile
  setUser({
    id: userId,
    name: displayName,
    email: profile.email,
    frontlineId: salesOrganization
  } as User)

  await Promise.all([
    updateConfigurationsPromise([
      { ...frontlineConfig, id: 'frontline' },
      { ...profile, id: 'profile' },
      { ...settings, id: 'settings' }
    ])
  ])
  return data
}
